import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { qk } from "@linkhr/api-client";
import { resolveTenant } from "@linkhr/auth";
import { env } from "../env";

const searchSchema = z.object({
  c: z.string().min(1).optional(),
  // Deep-linked composer seed. Consumed once by AssistantPage, then stripped.
  // Without this entry zod strips the unknown param before the page sees it.
  ask: z.string().min(1).optional(),
});

/** Shape of the bootstrap entry this guard reads out of the query cache. */
interface CachedPermissions {
  features?: Record<string, boolean>;
}

export const Route = createFileRoute("/_auth/me/assistant")({
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: ({ context }) => {
    // Build-time kill switch — final, nothing to await.
    if (!env.VITE_ASSISTANT_ENABLED) throw redirect({ to: "/me" });

    // Runtime entitlement. Talking to the assistant needs no permission of its
    // own (the server enforces conversation ownership); what gates it is the
    // tenant's `assistant_employee` key (CC-430).
    //
    // CACHE READ, not a fresh fetch: the router context carries only
    // { auth, queryClient } — the api client lives in React context, so a
    // query-options factory cannot be called from here. `_auth` keeps this
    // entry live (it mounts `useEntitlements` and revalidates on route change),
    // so it is populated for any in-app navigation.
    const { queryClient } = context as {
      queryClient: {
        getQueryData: (key: readonly unknown[]) => CachedPermissions | undefined;
      };
    };
    const slug = resolveTenant({ fallbackSlug: env.VITE_DEV_TENANT_SLUG });
    const cached = queryClient.getQueryData(qk.mePermissions.detail(slug));

    // A COLD cache (hard refresh straight onto this URL) is deliberately let
    // through rather than redirected: an absent answer is not a "no", and
    // bouncing a legitimate deep link before the bootstrap lands would be worse.
    // The route COMPONENT closes that window — it holds the page back until the
    // entitlement resolves, so nothing reaches the assistant API meanwhile.
    if (cached !== undefined && cached.features?.["assistant_employee"] !== true) {
      throw redirect({ to: "/me" });
    }
  },
  staticData: { breadcrumb: { labelKey: "assistant:page.breadcrumb" } },
});
